"use client";

import React, { useEffect, useState } from "react";
import { useMap } from "@/features/map/MapProvider";
import type { NewRequestMapPinContext } from "@/features/workplace-requests/views/queue/NewRequestWizard";

// Lazy-loaded to keep the workplace-requests wizard out of the map's static graph
// (the wizard pulls the map back in via the resource finder).
const NewRequestWizard = React.lazy(() =>
  import("@/features/workplace-requests/views/queue/NewRequestWizard").then(
    (m) => ({ default: m.NewRequestWizard })
  )
);

interface PinState {
  /** Map / SVG user-space coordinates (survive pan & zoom). */
  mapX: number;
  mapY: number;
  officeId: string;
  officeName: string;
  floorId?: string;
  floorName?: string;
  /**
   * The `.full-map-container` element that owns this pin's map, captured at click
   * time. Used to render the wizard contained (backdrop scoped to the map) instead
   * of over the whole viewport.
   */
  container: HTMLElement | null;
}

interface MapPinRequestLayerProps {
  svgMapRef?: React.RefObject<SVGSVGElement | null | undefined>;
}

const CLICK_MOVE_THRESHOLD = 6; // px — distinguishes a click from a pan/drag.

export const MapPinRequestLayer: React.FC<MapPinRequestLayerProps> = ({
  svgMapRef
}) => {
  const { floor, currentOffice, isPinRequestMode, startDate, endDate } =
    useMap();
  const [pin, setPin] = useState<PinState | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  // Drop a pin when the user clicks an empty point on the map background — but only
  // while pin-request mode is on (toggled via the "New request" button,
  // MapPinRequestToggle). Dropping the pin opens the request wizard straight away.
  useEffect(() => {
    if (!isPinRequestMode) return;
    let downX = 0;
    let downY = 0;
    let downTarget: Element | null = null;
    const onDown = (e: MouseEvent) => {
      downX = e.clientX;
      downY = e.clientY;
      downTarget = e.target as Element | null;
    };
    const onUp = (e: MouseEvent) => {
      if (
        Math.hypot(e.clientX - downX, e.clientY - downY) > CLICK_MOVE_THRESHOLD
      ) {
        return; // a pan/drag, not a click
      }
      const target = e.target as Element | null;
      // Require both press and release on the map. Clicking an overlay (e.g. the
      // wizard backdrop) closes it on press, so the release lands on the now-revealed
      // map — without this it would drop a new pin and reopen the wizard.
      if (!downTarget?.closest(".map-container")) return;
      if (!target?.closest(".map-container")) return; // outside the map
      if (target.closest('[id^="interactive-"]')) return; // a resource node

      const svg = svgMapRef?.current;
      const ctm = svg?.getScreenCTM();
      if (!svg || !ctm) return;
      const point = svg.createSVGPoint();
      point.x = e.clientX;
      point.y = e.clientY;
      const mapPoint = point.matrixTransform(ctm.inverse());
      setPin({
        mapX: mapPoint.x,
        mapY: mapPoint.y,
        officeId: currentOffice.id,
        officeName: currentOffice.name,
        floorId: floor?.id,
        floorName: floor?.name,
        container: svg.closest(".full-map-container") as HTMLElement | null
      });
      setIsWizardOpen(true);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("mouseup", onUp);
    };
  }, [
    isPinRequestMode,
    svgMapRef,
    currentOffice.id,
    currentOffice.name,
    floor?.id,
    floor?.name
  ]);

  // The pin belongs to a floor/office — drop it when either changes.
  useEffect(() => {
    setPin(null);
    setIsWizardOpen(false);
  }, [floor?.id, currentOffice.id]);

  // Leaving pin-request mode clears any dropped pin.
  useEffect(() => {
    if (!isPinRequestMode) {
      setPin(null);
      setIsWizardOpen(false);
    }
  }, [isPinRequestMode]);

  if (!pin) return null;

  return (
    <React.Suspense fallback={null}>
      <NewRequestWizard
        isOpen={isWizardOpen}
        onOpenChange={(open) => {
          setIsWizardOpen(open);
          // Closing the wizard clears the pin so the next click starts fresh.
          if (!open) setPin(null);
        }}
        contained={!!pin.container}
        portalContainer={pin.container}
        mapPin={
          {
            officeId: pin.officeId,
            officeName: pin.officeName,
            floorId: pin.floorId,
            floorName: pin.floorName,
            x: pin.mapX,
            y: pin.mapY
          } satisfies NewRequestMapPinContext
        }
        // The date and time the map is showing: a reservation question
        // starts on them, whichever resource is picked for the pin later.
        reservationWhen={
          startDate
            ? { days: [startDate], start: startDate, end: endDate }
            : undefined
        }
      />
    </React.Suspense>
  );
};
